import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  FormErrorMessage,
  HStack,
  Input,
  VStack,
  Grid,
  Spinner,
  Text,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  IconButton,
} from "@chakra-ui/react";
import { Icon } from "@chakra-ui/react";
import { Upload, X } from "lucide-react";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/gif",
  "image/webp",
] as const;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_ATTACHMENT_FILES = 10;
const ACCEPT_ATTR = "image/jpeg,image/png,image/gif,image/webp,.jpeg,.jpg,.png,.gif,.webp";
const DOWNLOAD_REVOKE_DELAY_MS = 500;

/** If date is ISO string, parse and return yyyy-mm-dd in local time; otherwise return as-is. */
function normalizeApiDate(dateRaw: unknown): string {
  if (dateRaw == null) return "";
  if (typeof dateRaw !== "string") return "";
  const s = dateRaw.trim();
  if (!s) return "";
  if (s.includes("T")) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.substring(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return s.length >= 10 ? s.substring(0, 10) : s;
}

export interface DownloadBillingFormProps {
  /** Billing Statement ID (optional). When missing or invalid, form does not fetch, attach, or download. */
  billingStatementId?: number;
  onSuccess?: () => void;
  onCancel: () => void;
}

export function DownloadBillingForm({
  billingStatementId,
  onSuccess,
  onCancel,
}: DownloadBillingFormProps) {
  const colors = useThemeColors();
  const { showError, showSuccess } = useToastNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [displayBillingStatementNo, setDisplayBillingStatementNo] = useState("");
  const [displayRegisteredName, setDisplayRegisteredName] = useState("");
  const [displayPaymentTerm, setDisplayPaymentTerm] = useState("");
  const [displayCiDate, setDisplayCiDate] = useState("");
  const [displayDueDate, setDisplayDueDate] = useState("");
  const [displayBusStyle, setDisplayBusStyle] = useState("");
  const [displayHasDetails, setDisplayHasDetails] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);

  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const hasAttachments = attachmentFiles.length > 0;

  // Valid only if provided and is a finite number; no fetch/attach/download when invalid
  const safeBillingStatementId =
    billingStatementId != null && typeof billingStatementId === "number" && Number.isFinite(billingStatementId)
      ? billingStatementId
      : null;

  function validateAttachmentFile(file: File): string | null {
    const type = file.type?.toLowerCase() ?? "";
    if (!ALLOWED_IMAGE_TYPES.includes(type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
      return `"${file.name}": must be JPEG, PNG, JPG, GIF, or WebP.`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `"${file.name}": must be 5MB or less.`;
    }
    return null;
  }

  function handleAttachmentChange(e: React.ChangeEvent<HTMLInputElement>) {
    setAttachmentError(null);
    const input = e.target;
    const fileList = input.files;
    if (!fileList || fileList.length === 0) return;

    const next: File[] = [];
    const errors: string[] = [];
    for (let i = 0; i < fileList.length; i++) {
      if (next.length >= MAX_ATTACHMENT_FILES) {
        errors.push(`Maximum ${MAX_ATTACHMENT_FILES} files allowed.`);
        break;
      }
      const file = fileList[i];
      const err = validateAttachmentFile(file);
      if (err) errors.push(err);
      else next.push(file);
    }
    if (errors.length > 0) {
      setAttachmentError(errors.slice(0, 3).join(" "));
    }
    setAttachmentFiles((prev) => {
      const combined = [...prev, ...next].slice(0, MAX_ATTACHMENT_FILES);
      return combined;
    });
    input.value = "";
  }

  function removeAttachment(index: number) {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  }

  // Always fetch billing statement by billingStatementId and autofill read-only fields
  useEffect(() => {
    if (safeBillingStatementId == null) return;
    isMountedRef.current = true;
    const fetchBillingStatement = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/billing-statements/${safeBillingStatementId}`);
        if (!isMountedRef.current) return;
        const data = response.data?.data ?? response.data ?? {};

        const billingStatementNo =
          data.billing_statement_no ?? (data as Record<string, unknown>).billing_statement_no ?? "";
        const soa = data.statement_of_account ?? (data as Record<string, unknown>).statement_of_account;
        const shippingLine = data.shipping_line ?? soa?.shipping_line ?? (soa as Record<string, unknown>)?.shipping_line;
        const registeredName =
          shippingLine?.name ?? (shippingLine as Record<string, unknown>)?.name ?? "";

        const paymentTerm = data.payment_term ?? (data as Record<string, unknown>).payment_term ?? "";
        const ciDateRaw = data.ci_date ?? (data as Record<string, unknown>).ci_date;
        const dueDateRaw = data.due_date ?? (data as Record<string, unknown>).due_date;
        const busStyle = data.bus_style ?? (data as Record<string, unknown>).bus_style ?? "";
        const hasDetails = data.has_details ?? (data as Record<string, unknown>).has_details ?? false;

        setDisplayBillingStatementNo(String(billingStatementNo));
        setDisplayRegisteredName(String(registeredName));
        setDisplayPaymentTerm(String(paymentTerm));
        setDisplayCiDate(normalizeApiDate(ciDateRaw));
        setDisplayDueDate(normalizeApiDate(dueDateRaw));
        setDisplayBusStyle(String(busStyle));
        setDisplayHasDetails(hasDetails === true);
      } catch (error: unknown) {
        if (isMountedRef.current) showError(error);
      } finally {
        if (isMountedRef.current) setIsLoading(false);
      }
    };

    fetchBillingStatement();
    return () => {
      isMountedRef.current = false;
    };
  }, [safeBillingStatementId, showError]);

  const handleDownload = async (format: "pdf" | "csv") => {
    if (safeBillingStatementId == null) {
      showError(new Error("Billing Statement ID is required to download."));
      return;
    }

    const setIsDownloading =
      format === "pdf" ? setIsDownloadingPdf : setIsDownloadingCsv;
    const downloadUrl =
      format === "pdf"
        ? `/billing-statements/${safeBillingStatementId}/download`
        : `/billing-statements/${safeBillingStatementId}/download-csv`;
    const fileExtension = format === "pdf" ? "pdf" : "csv";
    const successMessage =
      format === "pdf"
        ? "Billing Statement PDF downloaded successfully."
        : "Billing Statement CSV downloaded successfully.";

    setIsDownloading(true);
    let objectUrl: string | null = null;
    const includeAttachments = hasAttachments;

    try {
      if (includeAttachments && attachmentFiles.length > 0) {
        setIsUploadingAttachments(true);
        try {
          const formData = new FormData();
          attachmentFiles.forEach((file) => {
            formData.append("images[]", file, file.name);
          });
          await apiClient.request({
            method: "POST",
            url: "/soa-and-billing/attachments",
            data: formData,
            timeout: 60000,
            headers: {
              "Content-Type": undefined,
            },
          });
        } catch (uploadError: unknown) {
          showError(uploadError as Error);
          return;
        } finally {
          setIsUploadingAttachments(false);
        }
      }

      const response = await apiClient.get(downloadUrl, {
        responseType: "blob",
        timeout: 60000,
        params: { include_attachments: includeAttachments },
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("Invalid response: expected a file.");
      }

      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.setAttribute(
        "download",
        `${displayBillingStatementNo || "billing-statement"}.${fileExtension}`,
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.setTimeout(() => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }, DOWNLOAD_REVOKE_DELAY_MS);

      if (isMountedRef.current) {
        showSuccess("Success", successMessage);
        onSuccess?.();
      }
    } catch (error: unknown) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      if (isMountedRef.current) showError(error as Error);
    } finally {
      if (isMountedRef.current) {
        setIsDownloading(false);
        setIsUploadingAttachments(false);
      }
    }
  };

  if (safeBillingStatementId == null) {
    return (
      <Box p={4}>
        <Text color={colors.textSecondary}>
          {billingStatementId == null
            ? "No billing statement selected. Provide a billing statement ID to download."
            : "Invalid billing statement ID. Cannot fetch, attach, or download."}
        </Text>
        <Button mt={4} onClick={onCancel} variant="outline" size="sm">
          Cancel
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color={colors.text}>
          Loading billing statement...
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Read-only billing statement fields (same as BillingStatementForm) */}
      <Box>
        <VStack spacing={4} align="stretch">
          {/* Billed To (read-only) */}
          <Grid
            templateColumns={{ base: "1fr", md: "3fr 9fr" }}
            gap={4}
            alignItems="start"
          >
            <FormLabel pt={2} fontWeight="medium" color={colors.text}>
              Billed To:
            </FormLabel>
            <FormControl>
              <Input
                value={displayRegisteredName}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
          </Grid>

          {/* Billing Statement No (read-only) */}
          <Grid
            templateColumns={{ base: "1fr", md: "3fr 9fr" }}
            gap={4}
            alignItems="start"
          >
            <FormLabel pt={2} fontWeight="medium" color={colors.text}>
              Billing Statement No:
            </FormLabel>
            <FormControl>
              <Input
                value={displayBillingStatementNo}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
          </Grid>

          {/* Payment Term (read-only) */}
          <Grid
            templateColumns={{ base: "1fr", md: "3fr 9fr" }}
            gap={4}
            alignItems="start"
          >
            <FormLabel pt={2} fontWeight="medium" color={colors.text}>
              Payment Term:
            </FormLabel>
            <FormControl>
              <Input
                value={displayPaymentTerm}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
          </Grid>

          {/* CI Date and Due Date side by side (read-only) */}
          <Grid
            templateColumns={{ base: "1fr", md: "1fr 1fr" }}
            gap={4}
            alignItems="start"
          >
            <FormControl>
              <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                CI Date:
              </FormLabel>
              <Input
                value={displayCiDate}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
            <FormControl>
              <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                Due Date:
              </FormLabel>
              <Input
                value={displayDueDate}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
          </Grid>

          {/* Business Style, Has Details — one row (read-only) */}
          <Grid
            templateColumns={{ base: "1fr", md: "1fr 1fr" }}
            gap={4}
            alignItems="flex-end"
          >
            <FormControl>
              <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                Business Style:
              </FormLabel>
              <Input
                value={displayBusStyle}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
            <FormControl>
              <FormLabel pt={2} fontWeight="medium" color={colors.text}>
                Has Details
              </FormLabel>
              <Input
                value={displayHasDetails ? "Yes" : "No"}
                readOnly
                bg={colors.sectionBg}
                borderColor={colors.border}
                color={colors.text}
              />
            </FormControl>
          </Grid>
        </VStack>
      </Box>

      {/* Attachments (optional) */}
      <FormControl isInvalid={!!attachmentError}>
        <VStack align="stretch" spacing={3}>
          <FormLabel fontWeight="medium" color={colors.text}>
            Attachments (optional)
          </FormLabel>
          <FormHelperText color={colors.textSecondary} mt={0}>
            JPEG, PNG, JPG, GIF, or WebP. Max 5MB each. Max {MAX_ATTACHMENT_FILES} files.
          </FormHelperText>
          <HStack spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              leftIcon={<Icon as={Upload} boxSize={4} />}
              onClick={() => fileInputRef.current?.click()}
              isDisabled={attachmentFiles.length >= MAX_ATTACHMENT_FILES}
              variant="outline"
              borderColor={colors.border}
              color={colors.text}
            >
              Choose images
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              multiple
              onChange={handleAttachmentChange}
              style={{ display: "none" }}
              aria-hidden
            />
            {attachmentFiles.length > 0 && (
              <Text fontSize="sm" color={colors.textSecondary}>
                {attachmentFiles.length} file(s) selected
              </Text>
            )}
          </HStack>
          <FormErrorMessage>{attachmentError ?? ""}</FormErrorMessage>
          {attachmentFiles.length > 0 && (
            <Wrap spacing={2}>
              {attachmentFiles.map((file, index) => (
                <WrapItem key={`${file.name}-${index}`}>
                  <Tag size="md" colorScheme="blue" borderRadius="full" gap={1}>
                    <TagLabel maxW="120px" noOfLines={1} title={file.name}>
                      {file.name}
                    </TagLabel>
                    <IconButton
                      aria-label={`Remove ${file.name}`}
                      size="xs"
                      variant="ghost"
                      icon={<Icon as={X} boxSize={3} />}
                      onClick={() => removeAttachment(index)}
                      minW={6}
                      minH={6}
                    />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </VStack>
      </FormControl>

      {/* Action Buttons */}
      <HStack spacing={{ base: 2, md: 4 }} justify="flex-end" flexWrap="wrap" pt={2}>
        <Button
          variant="outline"
          onClick={onCancel}
          borderColor={colors.border}
          color={colors.text}
          size={{ base: "xs", sm: "sm", md: "md" }}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          px={{ base: 2, sm: 3, md: 4 }}
          whiteSpace="nowrap"
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleDownload("pdf")}
          colorScheme="blue"
          isLoading={isDownloadingPdf}
          loadingText={isUploadingAttachments ? "Uploading..." : "Downloading..."}
          isDisabled={
            safeBillingStatementId == null ||
            isDownloadingCsv ||
            isUploadingAttachments
          }
          size={{ base: "xs", sm: "sm", md: "md" }}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          px={{ base: 2, sm: 3, md: 4 }}
          whiteSpace="nowrap"
        >
          Download PDF
        </Button>
        <Button
          onClick={() => handleDownload("csv")}
          colorScheme="blue"
          isLoading={isDownloadingCsv}
          loadingText={isUploadingAttachments ? "Uploading..." : "Downloading..."}
          isDisabled={
            safeBillingStatementId == null ||
            isDownloadingPdf ||
            isUploadingAttachments
          }
          size={{ base: "xs", sm: "sm", md: "md" }}
          fontSize={{ base: "xs", sm: "sm", md: "md" }}
          px={{ base: 2, sm: 3, md: 4 }}
          whiteSpace="nowrap"
        >
          Download CSV
        </Button>
      </HStack>
    </VStack>
  );
}
